<template>
  <el-dialog
    title="拆分房屋"
    :visible.sync="dialogVisible"
    :width="dialogWidth"
    @close="handleClose"
    append-to-body
  >
    <!-- 原房屋信息 -->
    <el-card class="mb-20">
      <div slot="header" class="clearfix">
        <span>原房屋信息</span>
      </div>
      <el-form :model="originalRoom" label-width="120px" size="small">
        <el-row :gutter="20">
          <el-col :span="12" :xs="24" :sm="12">
            <el-form-item label="房屋编号">
              {{ originalRoom.roomId || "-" }}
            </el-form-item>
          </el-col>
          <el-col :span="12" :xs="24" :sm="12">
            <el-form-item label="房屋名称">
              {{ originalRoom.floorNum || "-" }}-{{
                originalRoom.unitNum || "-"
              }}-{{ originalRoom.roomNum || "-" }}
            </el-form-item>
          </el-col>
          <el-col :span="12" :xs="24" :sm="12">
            <el-form-item label="房屋面积">
              {{ formatNumber(originalRoom.roomArea) }}
              {{ $t("roomList.squareMeter") }}
            </el-form-item>
          </el-col>
          <el-col :span="12" :xs="24" :sm="12">
            <el-form-item label="剩余面积（预估）">
              <span
                class="remaining-area"
                :class="{ 'text-danger': remainingArea < 0 }"
              >
                {{ formatNumber(Math.max(0, remainingArea)) }}
                {{ $t("roomList.squareMeter") }}
              </span>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>

    <!-- 拆分房屋列表 -->
    <el-card class="mb-20">
      <div slot="header" class="clearfix">
        <span>拆分房屋信息</span>
        <el-button
          type="primary"
          size="small"
          class="float-right"
          @click="addSplitRoom"
        >
          <i class="el-icon-plus"></i> 添加拆分房屋
        </el-button>
      </div>

      <!-- 修复表格布局：增加间距、调整列宽 -->
      <el-table
        :data="splitRooms"
        border
        stripe
        style="width: 100%; margin-bottom: 10px"
        :loading="tableLoading"
        size="small"
      >
        <el-table-column prop="roomName" label="房屋名称" min-width="180">
          <template slot-scope="scope">
            <el-input
              v-model="scope.row.roomName"
              size="small"
              placeholder="请输入房屋名称"
              @blur="checkRoomNameDuplicate(scope.$index)"
              style="width: 100%"
            >
              <template slot="suffix">
                <i
                  v-if="scope.row.nameDuplicate"
                  class="el-icon-warning text-danger"
                  title="房屋名称重复"
                ></i>
              </template>
            </el-input>
          </template>
        </el-table-column>
        <el-table-column prop="roomArea" label="房屋面积(㎡)" min-width="120">
          <template slot-scope="scope">
            <el-input
              v-model.number="scope.row.roomArea"
              size="small"
              type="number"
              placeholder="请输入房屋面积"
              @input="debounceCalculateTotalArea"
              min="0.01"
              step="0.01"
              style="width: 100%"
            >
              <template slot="suffix">
                <i
                  v-if="scope.row.areaInvalid"
                  class="el-icon-warning text-danger"
                  title="面积必须大于0"
                ></i>
              </template>
            </el-input>
          </template>
        </el-table-column>
        <el-table-column label="操作" min-width="80" align="center">
          <template slot-scope="scope">
            <el-button
              type="danger"
              size="mini"
              @click="removeSplitRoom(scope.$index)"
              :disabled="splitRooms.length <= 1"
            >
              删除
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 面积汇总：修复排版 -->
      <div class="mt-10 text-right">
        <div class="area-summary">
          <span class="area-label">拆分总面积：</span>
          <span class="area-value">
            {{ formatNumber(totalRoomArea) }} {{ $t("roomList.squareMeter") }}
          </span>
          <span v-if="isAreaValid" class="text-success ml-10"
            >(拆分面积有效)</span
          >
          <span v-else class="text-danger ml-10">(拆分面积等于原房屋面积)</span>
        </div>
      </div>
    </el-card>

    <!-- 已拆分房屋列表：修复时间戳、布局 -->
    <el-card>
      <div slot="header" class="clearfix">
        <span>已拆分房屋列表</span>
      </div>
      <el-table
        :data="formattedSplitHistory"
        border
        stripe
        style="width: 100%"
        :loading="historyLoading"
        size="small"
      >
        <el-table-column
          prop="roomName"
          label="房屋名称"
          min-width="180"
        ></el-table-column>
        <el-table-column
          prop="roomArea"
          label="房屋面积(㎡)"
          min-width="120"
          align="center"
        ></el-table-column>
        <el-table-column
          prop="createTime"
          label="拆分时间"
          min-width="180"
          align="center"
        ></el-table-column>
      </el-table>
    </el-card>

    <div slot="footer" class="dialog-footer">
      <el-button @click="dialogVisible = false">{{
        $t("common.cancel")
      }}</el-button>
      <el-button
        type="primary"
        @click="handleSplit"
        :disabled="!isAreaValid || loading"
      >
        {{ $t("common.confirm") }}
        <i v-if="loading" class="el-icon-loading"></i>
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
// 替换lodash的debounce（避免依赖缺失）
function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

import { getSplitRooms, saveSplitRoom } from "@/api/room/splitRoomApi";

export default {
  name: "SplitRoom",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    room: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      dialogVisible: this.visible,
      originalRoom: {},
      splitRooms: [
        { roomName: "", roomArea: 0, nameDuplicate: false, areaInvalid: false },
      ],
      splitHistory: [],
      totalRoomArea: 0,
      loading: false,
      historyLoading: false,
      tableLoading: false,
      dialogWidth: "800px",
      resizeHandler: null,
    };
  },
  computed: {
    remainingArea() {
      const originalArea = parseFloat(this.originalRoom.roomArea) || 0;
      return originalArea - this.totalRoomArea;
    },
    isAreaValid() {
      const originalArea = parseFloat(this.originalRoom.roomArea) || 0;
      // 原房屋面积必须有效
      if (originalArea <= 0.001) return false;

      // 修改点：拆分总面积需 > 0 且 <= 原房屋面积
      // 增加 0.001 的容忍度是为了防止浮点数计算误差导致 10.00 变成 10.00000001 而验证失败
      const hasValidTotal =
        this.totalRoomArea > 0.001 &&
        this.totalRoomArea <= originalArea + 0.001;

      const hasValidRooms = this.splitRooms.every((room) => {
        const roomNameValid = room.roomName.trim() !== "";
        const roomAreaValid = parseFloat(room.roomArea) > 0.001;

        room.nameDuplicate =
          !roomNameValid || this.checkNameDuplicate(room.roomName);
        room.areaInvalid = !roomAreaValid;

        return roomNameValid && roomAreaValid;
      });

      return hasValidTotal && hasValidRooms;
    },
    // 格式化已拆分列表的时间戳
    formattedSplitHistory() {
      return (this.splitHistory || []).map((item) => ({
        ...item,
        createTime: this.formatTime(item.createTime),
      }));
    },
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal;
      if (newVal) {
        this.initData();
        this.initResponsive();
      }
    },
    dialogVisible(newVal) {
      this.$emit("update:visible", newVal);
      if (!newVal) {
        this.cleanup();
      }
    },
  },
  mounted() {
    this.debounceCalculateTotalArea = debounce(this.calculateTotalArea, 300);
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    initResponsive() {
      this.resizeHandler = () => {
        this.dialogWidth = window.innerWidth < 900 ? "95%" : "800px";
      };
      window.addEventListener("resize", this.resizeHandler);
      this.resizeHandler();
    },
    cleanup() {
      if (this.resizeHandler) {
        window.removeEventListener("resize", this.resizeHandler);
      }
      this.splitRooms = [
        { roomName: "", roomArea: 0, nameDuplicate: false, areaInvalid: false },
      ];
      this.splitHistory = [];
      this.totalRoomArea = 0;
    },
    // 格式化数字（保留两位小数）
    formatNumber(num) {
      const n = parseFloat(num);
      return isNaN(n) ? "0.00" : n.toFixed(2);
    },
    // 时间戳转可读日期
    formatTime(timestamp) {
      if (!timestamp) return "-";
      // 兼容秒/毫秒级时间戳
      const ts =
        timestamp.toString().length === 10 ? timestamp * 1000 : timestamp;
      return new Date(ts).toLocaleString();
    },
    checkNameDuplicate(name) {
      const trimName = name.trim() || "";
      if (!trimName) return false;
      return (
        this.splitRooms.filter((room) => room.roomName.trim() === trimName)
          .length > 1
      );
    },
    checkRoomNameDuplicate(index) {
      const room = this.splitRooms[index];
      room.nameDuplicate = this.checkNameDuplicate(room.roomName);
      if (room.nameDuplicate) {
        this.$message.warning("房屋名称不能重复");
      }
    },
    initData() {
      this.originalRoom = {
        ...this.room,
        roomArea: parseFloat(this.room.roomArea) || 0,
      };

      const defaultArea = Math.max(
        1,
        (this.originalRoom.roomArea / 3).toFixed(2) * 1,
      );
      this.splitRooms = [
        {
          roomName: "",
          roomArea: defaultArea,
          nameDuplicate: false,
          areaInvalid: false,
        },
      ];

      this.loadSplitHistory();
      this.calculateTotalArea();
    },
    addSplitRoom() {
      this.splitRooms.push({
        roomName: "",
        roomArea: 0,
        nameDuplicate: false,
        areaInvalid: false,
      });
    },
    removeSplitRoom(index) {
      if (this.splitRooms.length <= 1) {
        this.$message.warning("至少保留一个拆分房屋");
        return;
      }
      this.splitRooms.splice(index, 1);
      this.calculateTotalArea();
    },
    calculateTotalArea() {
      this.tableLoading = true;
      try {
        this.totalRoomArea = parseFloat(
          this.splitRooms
            .reduce((sum, item) => sum + (parseFloat(item.roomArea) || 0), 0)
            .toFixed(2),
        );
      } catch (e) {
        this.totalRoomArea = 0;
      } finally {
        this.tableLoading = false;
      }
    },
    async loadSplitHistory() {
      this.historyLoading = true;
      try {
        const params = {
          originalRoomId: this.originalRoom.roomId,
          page: 0,
          row: 100,
        };

        const res = await getSplitRooms(params);
        this.splitHistory = res.splitRooms || [];
      } catch (error) {
        console.error("加载拆分历史失败:", error);
        this.$message.error("加载拆分历史失败");
        this.splitHistory = [];
      } finally {
        this.historyLoading = false;
      }
    },
    async handleSplit() {
      if (!this.isAreaValid) {
        this.$message.warning("请检查拆分房屋信息是否有效");
        return;
      }

      try {
        this.loading = true;

        const params = {
          communityId: this.originalRoom.communityId,
          originalRoomId: this.originalRoom.roomId,
          splitRooms: this.splitRooms.map((room) => ({
            roomName: room.roomName.trim(),
            roomArea: parseFloat(room.roomArea).toFixed(2),
            parentRoomId: this.originalRoom.roomId,
          })),
        };

        await saveSplitRoom(params);

        this.$message.success("房屋拆分成功");
        this.dialogVisible = false;
        this.$emit("split-success");
      } catch (error) {
        console.error("房屋拆分失败:", error);
        this.$message.error(error.message || "房屋拆分失败");
      } finally {
        this.loading = false;
      }
    },
    handleClose() {
      this.dialogVisible = false;
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
/* 修复样式穿透问题：用::v-deep穿透scoped */
::v-deep .mb-20 {
  margin-bottom: 20px !important;
}

::v-deep .mt-10 {
  margin-top: 10px !important;
}

::v-deep .ml-10 {
  margin-left: 10px !important;
}

::v-deep .text-success {
  color: #67c23a !important;
}

::v-deep .text-danger {
  color: #f56c6c !important;
}

::v-deep .float-right {
  float: right !important;
}

::v-deep .area-summary {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 5px 0;
  flex-wrap: wrap;
}

::v-deep .area-label {
  margin-right: 5px;
  font-weight: bold;
}

::v-deep .area-value {
  margin-right: 5px;
}

::v-deep .remaining-area {
  font-weight: 500;
}

/* 响应式适配 */
@media screen and (max-width: 768px) {
  ::v-deep .el-dialog {
    width: 95% !important;
  }

  ::v-deep .el-table-column {
    min-width: 80px !important;
  }
}
</style>
